const AUTHORIZE_QUERY_STORAGE_KEY = 'vadio_oauth_authorize_query';
const SELECTED_COMPANY_STORAGE_KEY = 'vadio_oauth_selected_company_id';
const SIGNED_QUERY_REQUIRED_PARAMS = ['client_id', 'redirect_uri', 'sig'] as const;

export type AuthorizeQueryRead = { kind: 'none' } | { kind: 'expired' } | { kind: 'valid'; query: string };

type ReadAuthorizeQueryInput = {
	search: string;
	nowMs?: number;
};

export const readAuthorizeQuery = ({ search, nowMs = Date.now() }: ReadAuthorizeQueryInput): AuthorizeQueryRead => {
	const query = search.startsWith('?') ? search.slice(1) : search;
	const params = new URLSearchParams(query);
	const signed = SIGNED_QUERY_REQUIRED_PARAMS.every((name) => Boolean(params.get(name)));
	if (!signed) return { kind: 'none' };
	const exp = Number(params.get('exp'));
	if (Number.isFinite(exp) && exp > 0 && exp * 1000 <= nowMs) return { kind: 'expired' };
	return { kind: 'valid', query };
};

export type AuthorizeQueryFields = {
	clientId: string;
	redirectUri: string;
	scope: string;
};

export const readAuthorizeQueryFields = (query: string): AuthorizeQueryFields => {
	const params = new URLSearchParams(query);
	return {
		clientId: params.get('client_id') ?? '',
		redirectUri: params.get('redirect_uri') ?? '',
		scope: params.get('scope') ?? '',
	};
};

const storage = () => window.sessionStorage;

export const captureAuthorizeQuery = ({ search }: { search: string }): AuthorizeQueryRead => {
	const read = readAuthorizeQuery({ search });
	if (read.kind === 'valid') storage().setItem(AUTHORIZE_QUERY_STORAGE_KEY, read.query);
	return read;
};

export const getCurrentAuthorizeQuery = (): AuthorizeQueryRead => {
	const fromUrl = readAuthorizeQuery({ search: window.location.search });
	if (fromUrl.kind !== 'none') return fromUrl;
	const stored = storage().getItem(AUTHORIZE_QUERY_STORAGE_KEY);
	if (!stored) return { kind: 'none' };
	return readAuthorizeQuery({ search: stored });
};

export const clearAuthorizeQuery = () => {
	storage().removeItem(AUTHORIZE_QUERY_STORAGE_KEY);
};

export const rememberSelectedCompany = (companyId: string) => {
	storage().setItem(SELECTED_COMPANY_STORAGE_KEY, companyId);
};

export const getRememberedCompany = () => storage().getItem(SELECTED_COMPANY_STORAGE_KEY) ?? null;

export const clearRememberedCompany = () => {
	storage().removeItem(SELECTED_COMPANY_STORAGE_KEY);
};

export const clearOAuthFlowState = () => {
	clearAuthorizeQuery();
	clearRememberedCompany();
};
